/* eslint-disable import/no-cycle */
/**
 * Dynamic config-panel builder for generic integration widgets.
 *
 * Unlike native widgets (static config maps), an integration widget's panel is
 * built PER ELEMENT from the block's `data-schema` (the widget's config_schema)
 * — the fields differ per widget. The panel is composed of:
 *   1. a "Configuration" group generated from config_schema (one field/param), and
 *   2. the SAME style groups as the native image widget (inherited), since an
 *      integration image widget is, structurally, an <img> in a <table>.
 *
 * Returns a ConfigurationPanelStructure shaped exactly like getWidgetConfigV4's
 * output, so it can replace `config` in the store's prepareStructureConfiguration.
 */
import {
  ConfigurationPanelStructure,
  ConfigurationPanelStructureItemEnum,
  TemplateStructureEnum,
  ComponentNameEnum,
  ElementPartTypeEnum,
  AlignValueEnum,
  StepperSignEnum,
} from '@/types';
import type { WidgetConfigField, WidgetConfigGroup } from '@/types/integrations';
import getColorFieldConfig from '@/components/template-builder/config/fields/color-config';
import getStepperFieldConfig from '@/components/template-builder/config/fields/stepper-config';
import getDropdownFieldConfig from '@/components/template-builder/config/fields/dropdown-config';
import getInputVariablesFieldConfig from '@/components/template-builder/config/fields/input-variables-config';
import getWidthFieldConfig from '@/components/template-builder/config/fields/width-config';
import getElementAlignFieldConfig from '@/components/template-builder/config/fields/element-align-config';
import getImageBorderRadiusFieldConfig from '@/components/template-builder/config/fields/image-border-radius-config';
import bordersFieldConfig from '@/components/template-builder/config/fields/borders-config';
import getWidgetCommonStylesGroup from '@/components/template-builder/config/fields/widget-common-styles-config';
import getConditionsAndRepetitionGroup from '@/components/template-builder/config/fields/conditions-repetition-group-helper';
import variables from '@/components/template-builder/utils/variables-list';
import { getCssPropertyByName } from '@/components/template-builder/getters';
import {
  setCssProperty,
  setCssPropertyAndRemoveIfZero,
  setImageBorderRadius,
  getIntegrationWidgetConfigValue,
  setIntegrationWidgetParam,
  getIntegrationWidgetColorValue,
  setIntegrationWidgetColorNoHash,
} from '@/components/template-builder/setters';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';
import { i18n } from '@/i18n';
import { pickLocalizedText } from '@/utils/localized-text';

/** Traduction synchrone (hors setup) : lit l'i18n global de l'app. */
const tGlobal = (key: string): string => (i18n.global as any).t(key);
/** Locale courante de l'app (ex. 'fr' | 'en'), fallback 'fr'. */
const currentLocale = (): string => {
  const loc = (i18n.global as any).locale;
  return String((loc && typeof loc === 'object' && 'value' in loc ? loc.value : loc) || 'fr');
};

/**
 * Sélectionne le texte d'un label localisé en respectant la LOCALE COURANTE via
 * l'utilitaire central `pickLocalizedText` (locale → en → fr → 1re valeur) — G4/G12,
 * remplace l'ancienne version FR-first buggée. `fallback` si aucune valeur.
 */
const localizeLabel = (label: unknown, fallback: string): string => {
  const text = pickLocalizedText(label as any, currentLocale());
  return text || fallback;
};

/** Map one config_schema field → a native config-panel field item. */
const buildParamField = (field: WidgetConfigField): any => {
  const base = {
    label: localizeLabel(field.label, field.key),
    selector: '{SELECTOR}',
    properties: [
      {
        name: field.key,
        getter: getIntegrationWidgetConfigValue,
        setters: [setIntegrationWidgetParam],
      },
    ],
  };

  // eslint-disable-next-line consistent-return
  const result: any = (() => {
    switch (field.type) {
      case 'color': {
        // strip_hash colours store/emit bare hex (for image URLs); the picker
        // still needs `#rrggbb`, so use the # ↔ no-# getter/setter pair.
        const colorBase = field.strip_hash
          ? {
            ...base,
            properties: [
              {
                name: field.key,
                getter: getIntegrationWidgetColorValue,
                setters: [setIntegrationWidgetColorNoHash],
              },
            ],
          }
          : base;
        const def = field.strip_hash
          ? `#${String(field.default ?? '000000').replace(/^#/, '')}`
          : String(field.default ?? '#000000');
        return { ...colorBase, ...getColorFieldConfig(def, false, false) };
      }
      case 'checkbox':
        return {
          ...base,
          componentName: ComponentNameEnum.CHECKBOX,
          options: { defaultValue: Boolean(field.default) },
        };
      case 'number': {
        const stepper = getStepperFieldConfig(StepperSignEnum.NONE, 1, 0, 100000);
        return {
          ...base,
          ...stepper,
          options: { ...stepper.options, defaultValue: Number(field.default ?? 0) },
        };
      }
      case 'select':
        return {
          ...base,
          ...getDropdownFieldConfig(
            (field.options || []).map((o) => ({
              value: o.value,
              label: localizeLabel(o.label, o.value),
            })),
            String(field.default ?? ''),
            false,
          ),
        };
      case 'datetime':
        // Reuse the native date/time picker (same component the native countdown
        // uses for its end date). Value stored as-is in data-config[key].
        return {
          ...base,
          componentName: ComponentNameEnum.DATETIME_PICKER,
          options: { defaultValue: String(field.default ?? '') },
          displayToggle: false,
        };
      case 'text':
      default:
        return {
          ...base,
          ...getInputVariablesFieldConfig(
            String(field.default ?? ''),
            variables.allVariables,
            false,
            !!field.supports_variables,
          ),
        };
    }
  })();

  // Conditional visibility + re-parse trigger — reuse the SAME mechanism the
  // native widgets use (the panel field-group filters items by isVisible). A
  // field with `refresh_fields` becomes the parent whose change re-parses the
  // panel; fields with `visible_when` show only for the parent's allowed values.
  if (field.refresh_fields) {
    result.fieldId = field.key;
    if (result.properties && result.properties[0]) {
      result.properties[0].refreshWidgetFields = true;
    }
  }
  if (field.visible_when) {
    const parentKey = field.visible_when.field;
    const allowed = field.visible_when.in || [];
    result.parentFieldId = parentKey;
    result.isVisible = (items: any[], parentFieldId: string) => {
      const parent = items.find((it) => it.fieldId === parentFieldId);
      const value = parent?.properties?.[0]?.value;
      return value == null ? true : allowed.includes(String(value));
    };
  }

  return result;
};

/** Alignment style item, shared by the image and html style tabs. */
const alignmentStyleItem = (): any => ({
  label: 'templateBuilder.fields.alignment',
  selector: '{SELECTOR} .spm_integration_widget_cell',
  properties: [{ name: 'text-align', getter: getCssPropertyByName, setters: [setCssProperty] }],
  ...getElementAlignFieldConfig('text-align', AlignValueEnum.CENTER),
});

/** Wrapper-border style item, shared by the image and html style tabs. */
const bordersStyleItem = (): any => ({
  label: 'templateBuilder.fields.borders',
  selector: '{SELECTOR}.spm_widget_integration',
  properties: [
    { name: 'border-color', getter: getCssPropertyByName, setters: [setCssProperty] },
    { name: 'border-style', getter: getCssPropertyByName, setters: [] },
    { name: 'border-width', getter: getCssPropertyByName, setters: [setCssPropertyAndRemoveIfZero] },
    { name: 'border-bottom-style', getter: getCssPropertyByName, setters: [setCssProperty] },
    { name: 'border-bottom-color', getter: getCssPropertyByName, setters: [] },
    { name: 'border-bottom-width', getter: getCssPropertyByName, setters: [] },
    { name: 'border-top-style', getter: getCssPropertyByName, setters: [setCssProperty] },
    { name: 'border-top-color', getter: getCssPropertyByName, setters: [] },
    { name: 'border-top-width', getter: getCssPropertyByName, setters: [] },
    { name: 'border-left-style', getter: getCssPropertyByName, setters: [setCssProperty] },
    { name: 'border-left-color', getter: getCssPropertyByName, setters: [] },
    { name: 'border-left-width', getter: getCssPropertyByName, setters: [] },
    { name: 'border-right-style', getter: getCssPropertyByName, setters: [setCssProperty] },
    { name: 'border-right-color', getter: getCssPropertyByName, setters: [] },
    { name: 'border-right-width', getter: getCssPropertyByName, setters: [] },
  ],
  ...bordersFieldConfig,
});

/** Style groups inherited from the native image widget (same <table>/<img>). */
const buildStylesTab = (): any => ({
  type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
  tabItemType: TemplateStructureEnum.GROUP,
  label: 'templateBuilder.configs.widgetStyles',
  canSave: true,
  elementPartType: ElementPartTypeEnum.STYLES,
  tabPanel: {
    items: [
      {
        type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
        label: 'templateBuilder.configs.widgetBlockStyles',
        selector: 'body #spm_body #{ID}',
        items: [
          {
            label: 'templateBuilder.fields.width',
            selector: '{SELECTOR} .spm_integration_widget_img',
            properties: [{ name: 'max-width', getter: getCssPropertyByName, setters: [setCssProperty] }],
            fieldId: 'imgWidth',
            ...getWidthFieldConfig(),
          },
          alignmentStyleItem(),
          {
            label: 'templateBuilder.fields.imageBorderRadius',
            selector: '{SELECTOR} .spm_integration_widget_img',
            properties: [{ name: 'border-radius', getter: getCssPropertyByName, setters: [setImageBorderRadius] }],
            ...getImageBorderRadiusFieldConfig(),
          },
          bordersStyleItem(),
        ],
      },
      getWidgetCommonStylesGroup('{SELECTOR} .spm_integration_widget_cell'),
    ],
  },
});

/**
 * Style groups for an html integration widget. The block is a rendered HTML
 * cell (no `<img>`), so only block-level styles apply: alignment, borders on
 * the wrapper, and common spacing on the content cell.
 */
const buildBlockStylesTab = (): any => ({
  type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
  tabItemType: TemplateStructureEnum.GROUP,
  label: 'templateBuilder.configs.widgetStyles',
  canSave: true,
  elementPartType: ElementPartTypeEnum.STYLES,
  tabPanel: {
    items: [
      {
        type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
        label: 'templateBuilder.configs.widgetBlockStyles',
        selector: 'body #spm_body #{ID}',
        items: [
          alignmentStyleItem(),
          bordersStyleItem(),
        ],
      },
      getWidgetCommonStylesGroup('{SELECTOR} .spm_integration_widget_cell'),
    ],
  },
});

/**
 * Build the full config-panel structure for the selected integration widget.
 * Returns null if the element / schema can't be read (caller falls back).
 */
export const buildIntegrationWidgetConfigStructure = (
  identifier: string,
  columnTabItem?: any,
  lineTabItem?: any,
): ConfigurationPanelStructure | null => {
  const el = getTemplateIframeDocument()?.getElementById(identifier) as HTMLElement | null;
  if (!el) return null;

  let groups: WidgetConfigGroup[] | null = null;
  let fields: WidgetConfigField[] = [];
  let styleGroups: WidgetConfigGroup[] | null = null;
  try {
    const schema = JSON.parse(el.getAttribute('data-schema') || '{}');
    if (Array.isArray(schema?.groups) && schema.groups.length > 0) {
      groups = schema.groups;
    } else if (Array.isArray(schema?.fields)) {
      fields = schema.fields;
    }
    if (Array.isArray(schema?.style_groups) && schema.style_groups.length > 0) {
      styleGroups = schema.style_groups;
    }
  } catch {
    fields = [];
  }

  const buildFieldsGroup = (label: string, flds: WidgetConfigField[]) => ({
    type: ConfigurationPanelStructureItemEnum.FIELDS_GROUP,
    label,
    selector: 'body #spm_body #{ID}',
    items: (flds || []).map(buildParamField),
  });

  // Flat `fields` → a single "Configuration" group. `groups` → a Configuration
  // section whose sub-groups render as horizontal tabs (like native widgets'
  // Paramètres / Labels).
  const configurationSection = groups
    ? {
      type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
      tabItemType: TemplateStructureEnum.GROUP,
      label: 'templateBuilder.configs.widgetConfiguration',
      tabPanel: {
        items: groups.map((g, i) => buildFieldsGroup(
          localizeLabel(g.label, `${tGlobal('templateBuilder.configs.widgetGroupFallback')} ${i + 1}`),
          g.fields,
        )),
      },
    }
    : buildFieldsGroup('templateBuilder.configs.widgetConfiguration', fields);

  // Generic native-like "Styles" tab — ALWAYS present. It carries the structural
  // block group ("Bloc": alignment, + width/border-radius for images) AND the
  // common "Widget" group (getWidgetCommonStylesGroup: background color/image,
  // padding, media-restrict) — exactly like a native widget.
  const renderType = el.getAttribute('data-render-type') || 'image';
  const stylesTab: any = renderType === 'html' ? buildBlockStylesTab() : buildStylesTab();
  // Integrator-declared `style_groups` feed data-config (image URL / renderer
  // config), not CSS. Add them as extra sub-groups of the SAME "Styles" tab,
  // BEFORE the generic block/"Widget" groups — so the common "Widget" styles
  // stay available even when the JSON declares its own style groups (instead of
  // the old behaviour where style_groups REPLACED the generic styles).
  if (styleGroups) {
    stylesTab.tabPanel.items = [
      ...styleGroups.map((g, i) => buildFieldsGroup(
        localizeLabel(g.label, `${tGlobal('templateBuilder.configs.widgetStyleFallback')} ${i + 1}`),
        g.fields,
      )),
      ...stylesTab.tabPanel.items,
    ];
  }

  // Panel header = the widget name; the source integration is shown as a badge
  // to its left (rendered by ConfigurationPanel from `integrationName` below).
  const widgetName = el.getAttribute('data-widget-name') || '';
  const integrationName = el.getAttribute('data-integration-name') || '';

  // Widget tab = config + styles + conditions, then the template's Colonne / Ligne
  // items (mirrors getWidgetConfigV4): the live-editor resolves their
  // {COLUMN_ID}/{LINE_ID} selectors to the block's parent column/line, so an
  // integration widget gets the same Colonne / Ligne controls as a native widget.
  const widgetTabItems: any[] = [
    configurationSection,
    stylesTab,
    getConditionsAndRepetitionGroup('body #spm_body #{ID}'),
  ];
  if (columnTabItem?.tabPanel?.items) widgetTabItems.push(...columnTabItem.tabPanel.items);
  if (lineTabItem?.tabPanel?.items) widgetTabItems.push(...lineTabItem.tabPanel.items);

  return {
    items: [
      {
        type: ConfigurationPanelStructureItemEnum.TAB_ITEM,
        tabItemType: TemplateStructureEnum.WIDGET,
        label: 'templateBuilder.configs.widget',
        // Custom field read by ConfigurationPanel to render the integration badge.
        integrationName,
        tabPanel: {
          label: widgetName || 'templateBuilder.widgetsNameBubble.integration-widget',
          items: widgetTabItems,
        },
      },
    ],
  } as unknown as ConfigurationPanelStructure;
};
